/**
 * Whether the QuickCare API is down, for the service-down page.
 *
 * The page replaces the whole app, so it may only appear when the service is
 * really down: the API cannot be reached, or it answers but its database
 * cannot. Never because of one bad request, the user's own connection, or a
 * health URL that does not reach the API.
 *
 * Every API call that fails at the transport level (fetch rejects), or comes
 * back through the edge as a 502/503/504, or as a 5xx that is not JSON (the
 * Google front end's HTML error page), reports here. A call that requestRetry
 * re-sends (an upstream 500/503 on a GET, Cloud Run's 429) reports only its
 * final answer, once the retries have run out, so a blip that a retry absorbs
 * never reports at all. A report only starts a confirmation: the health
 * endpoint is asked CONFIRMATIONS_BEFORE_DOWN times, CONFIRM_RETRY_MS apart,
 * and the page appears only if EVERY one of those checks proves an outage.
 * Each check reads as one of (see `checkHealth`):
 *
 *   - up:      a 2xx carrying the API's own `{ ok: true }` body.
 *   - down:    a 5xx — the API's 503 when its database round trip fails, or
 *              the proxy/edge 502/503/504 when the API cannot be reached — or
 *              no answer at all while the dashboard's own origin still
 *              answers, so the user is online and it is the API that is gone.
 *   - offline: no answer from the API and none from the dashboard's own
 *              origin either: the user's connection, not the service.
 *              Deliberately coarse: the origin is a static file on the
 *              hosting CDN, so a network that reaches the CDN but not the API
 *              (some corporate proxies, captive portals) reads as down. That
 *              is accepted; the page then tells the truth for that user.
 *   - unknown: any other answer (a 404, a 401, a 200 that is not the API's
 *              body). Something answered, so it is not an outage; it is not a
 *              pass either. It never contributes to the page. This is the
 *              guard that was missing on 11 Oct 2026: Cloud Run's front end
 *              answered /healthz with its own 404 for every check, so one
 *              failed request put a user on the page and no check could ever
 *              take them off it.
 *
 * Once down, the health endpoint is polled every HEALTH_POLL_MS, and checked
 * at once when the browser comes back online. Down and offline keep the page
 * — reloading into a dead connection helps no one — and anything else means
 * the outage is no longer proven, so `onRecovered` runs (the app reloads
 * itself).
 *
 * Framework-free on purpose: `api.js` reports into it, the React hook reads
 * it through useSyncExternalStore, and the tests drive it with a mocked
 * fetch and fake timers.
 */

const GATEWAY_STATUSES = new Set([502, 503, 504]);

export const HEALTH_POLL_MS = 20_000;

/** Wait for the health endpoint's answer. */
export const HEALTH_TIMEOUT_MS = 8_000;

/**
 * Wait for the dashboard's own origin, asked only when the health check got
 * no answer. A static file on the CDN, so it is short: one check takes at
 * most HEALTH_TIMEOUT_MS + ORIGIN_TIMEOUT_MS.
 */
export const ORIGIN_TIMEOUT_MS = 3_000;

/** Checks in a row that must each prove an outage before the page is shown. */
export const CONFIRMATIONS_BEFORE_DOWN = 3;

/** Gap between those checks, so a momentary blip cannot supply the whole streak. */
export const CONFIRM_RETRY_MS = 3_000;

/** What one health check proved. */
export const CHECK = Object.freeze({ UP: 'up', DOWN: 'down', OFFLINE: 'offline', UNKNOWN: 'unknown' });

/** Results that keep the page up once it is showing. */
const STILL_DOWN = new Set([CHECK.DOWN, CHECK.OFFLINE]);

const INITIAL_STATE = Object.freeze({ down: false, since: null, lastCheckedAt: null, reason: null, checking: false });

let healthUrl = '/healthcheck';
// Any file on the dashboard's own host; answering at all is the whole test.
let originUrl = '/favicon.ico';
let onRecovered = () => {};
let state = INITIAL_STATE;
let pendingReason = null;
let confirming = null;
let pollTimer = null;
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

const setState = (patch) => {
  state = { ...state, ...patch };
  notify();
};

/** Called once by api.js with the real health URL and the recovery action. */
export const configureServiceStatus = ({ healthUrl: url, originUrl: origin, onRecovered: recovered } = {}) => {
  if (url) healthUrl = url;
  if (origin) originUrl = origin;
  if (recovered) onRecovered = recovered;
};

export const getServiceStatus = () => state;

export const subscribeServiceStatus = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const contentTypeOf = (response) => String(
  (typeof response.headers?.get === 'function'
    ? response.headers.get('Content-Type')
    : response.headers?.['content-type']) || ''
);

const isJson = (response) => contentTypeOf(response).includes('application/json');

/** A response that means the service, not this endpoint, may be failing. */
export const isServiceFailure = (response) => {
  if (!response || typeof response.status !== 'number') return false;
  if (GATEWAY_STATUSES.has(response.status)) return true;
  if (response.status >= 500) return !isJson(response);
  return false;
};

/** A rejected request: DNS, connection refused, or a timeout before any answer. */
export const isTransportFailure = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError' || error.name === 'TimeoutError') return true;
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') return true;
  return error instanceof TypeError;
};

const fetchWithTimeout = async (url, init, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, cache: 'no-store', signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

/** Only the API's own body is a pass: a hosting fallback page can be a 200 too. */
const carriesHealthyBody = async (response) => {
  if (!isJson(response)) return false;
  try {
    const body = await response.json();
    return body?.ok === true;
  } catch {
    return false;
  }
};

/**
 * Does the dashboard's own host answer? Tells "the API is gone" from "this
 * user is offline" when the health check gets no answer. Any status counts:
 * the question is only whether the user's connection works.
 */
const originAnswers = async () => {
  try {
    await fetchWithTimeout(originUrl, { method: 'HEAD' }, ORIGIN_TIMEOUT_MS);
    return true;
  } catch {
    return false;
  }
};

/** One health check, read as up / down / offline / unknown (see the header). */
export const checkHealth = async () => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return CHECK.OFFLINE;
  let response;
  try {
    response = await fetchWithTimeout(healthUrl, { method: 'GET' }, HEALTH_TIMEOUT_MS);
  } catch {
    return (await originAnswers()) ? CHECK.DOWN : CHECK.OFFLINE;
  }
  if (response.status >= 500) return CHECK.DOWN;
  if (response.ok && await carriesHealthyBody(response)) return CHECK.UP;
  return CHECK.UNKNOWN;
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Back online while the page is up: check now rather than at the next poll.
const onBackOnline = () => { void confirm(); };

const stopPolling = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  if (typeof window !== 'undefined') window.removeEventListener('online', onBackOnline);
};

const markDown = (reason) => {
  if (state.down) return;
  setState({ down: true, since: Date.now(), reason });
  if (!pollTimer) pollTimer = setInterval(() => { void confirm(); }, HEALTH_POLL_MS);
  if (typeof window !== 'undefined') window.addEventListener('online', onBackOnline);
};

const markUp = () => {
  stopPolling();
  setState({ down: false, since: null, reason: null });
  onRecovered();
};

/**
 * Check the health endpoint until a check does not prove an outage, or the
 * streak is long enough to call it one. While already down a single check
 * decides: the streak only guards the transition into the page, and the poll
 * timer is the retry.
 */
const runConfirmation = async () => {
  const attempts = state.down ? 1 : CONFIRMATIONS_BEFORE_DOWN;
  let result = CHECK.UNKNOWN;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (attempt > 0) await delay(CONFIRM_RETRY_MS);
    result = await checkHealth();
    setState({ lastCheckedAt: Date.now() });
    if (result !== CHECK.DOWN) return result;
  }
  return result;
};

/** One confirmation at a time; concurrent reports and retries share it. */
const confirm = () => {
  if (confirming) return confirming;
  setState({ checking: true });
  confirming = runConfirmation()
    .then((result) => {
      setState({ checking: false });
      if (state.down) {
        if (!STILL_DOWN.has(result)) markUp();
      } else if (result === CHECK.DOWN) {
        markDown(pendingReason || 'health');
      }
      return result;
    })
    .finally(() => {
      confirming = null;
    });
  return confirming;
};

/**
 * api.js calls this for every failure that looks like the service itself.
 * Cheap while already down, and while a confirmation is in flight.
 */
export const reportServiceFailure = (reason) => {
  if (state.down) return;
  pendingReason = reason;
  void confirm();
};

/**
 * The page's "Try again".
 *
 * Resolves to the check's result, a CHECK value — a string, so always truthy:
 * test `=== CHECK.UP`, never the result itself.
 *
 * @returns {Promise<'up'|'down'|'offline'|'unknown'>}
 */
export const retryNow = () => confirm();

/** Tests only. */
export const resetServiceStatus = () => {
  stopPolling();
  confirming = null;
  pendingReason = null;
  state = INITIAL_STATE;
  listeners.clear();
};
